//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Map Layer Grid tab
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.7.0
//    001   03.10.25 Sean Flook       IMANN-1812 Initial version.
//    006   06.11.25 Sean Flook       IMANN-2092 Changes required to handle Vector Tile Layers.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";

import { GetMapLayersUrl } from "../configuration/ADSConfig";

import { Typography, Button, Tooltip, Avatar, Box, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ADSActionButton from "../components/ADSActionButton";

import AddIcon from "@mui/icons-material/Add";
import LayersIcon from "@mui/icons-material/Layers";

import {
  adsBlueA,
  adsLightBlue,
  adsDarkGrey10,
  adsDarkGrey20,
  adsMagenta,
  adsDarkBlue,
  adsPaleBlueB,
  adsRed,
} from "../utils/ADSColours";
import { blueButtonStyle, dataFormStyle, tooltipStyle } from "../utils/ADSStyles";
import { useTheme, styled } from "@mui/material/styles";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .visible-row": {
    "&:hover": {
      backgroundColor: adsPaleBlueB,
      color: adsBlueA,
      // cursor: "pointer",
    },
  },
  "& .hidden-row": {
    backgroundColor: adsDarkGrey10,
    "&:hover": {
      backgroundColor: adsDarkGrey20,
      color: adsBlueA,
      // cursor: "pointer",
    },
  },
}));

MapLayerGridTab.propTypes = {
  variant: PropTypes.oneOf(["base", "reference", "custom"]).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddMap: PropTypes.func.isRequired,
  onEditMap: PropTypes.func.isRequired,
  onDeleteMap: PropTypes.func.isRequired,
};

function MapLayerGridTab({ variant, data, onAddMap, onEditMap, onDeleteMap }) {
  const theme = useTheme();

  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);

  const [title, setTitle] = useState("");
  const [addTooltip, setAddTooltip] = useState("");
  const [addLabel, setAddLabel] = useState("");

  const [gridData, setGridData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const newLayer = useRef(false);

  /**
   * Method to display the action buttons for each row of the grid.
   *
   * @param {object} params The parameters passed into the method from the grid.
   * @returns {JSX.Element} The actions buttons for the row.
   */
  const displayActionButtons = (params) => {
    const moveLayer = async (currentLayerPosition, newLayerPosition) => {
      const mapLayerApiUrl = GetMapLayersUrl("PATCH", userContext.currentUser);

      if (mapLayerApiUrl) {
        const isNewLayer = newLayer.current;

        if (userContext.currentUser.showMessages)
          console.log(
            "[DEBUG] moveLayer",
            `${mapLayerApiUrl.url}/${params.id}/${currentLayerPosition < newLayerPosition ? "MoveUp" : "MoveDown"}`
          );

        await fetch(
          `${mapLayerApiUrl.url}/${params.id}/${currentLayerPosition < newLayerPosition ? "MoveUp" : "MoveDown"}`,
          {
            headers: mapLayerApiUrl.headers,
            crossDomain: true,
            method: mapLayerApiUrl.type,
          }
        )
          .then((res) => (res.ok ? res : Promise.reject(res)))
          .then((res) => res.json())
          .then((result) => {
            const currentRecord = data.find((x) => x.pkId === params.id);
            const adjacentRecord = data.find((x) => x.layerPosition === newLayerPosition);

            const modifiedRecords =
              currentRecord && adjacentRecord
                ? [
                    {
                      pkId: currentRecord.pkId,
                      layerId: currentRecord.layerId,
                      layerType: currentRecord.layerType,
                      layerPosition: newLayerPosition,
                      url: currentRecord.url,
                      title: currentRecord.title,
                      copyright: currentRecord.copyright,
                      displayInList: currentRecord.displayInList,
                      visible: currentRecord.visible,
                      minScale: currentRecord.minScale,
                      maxScale: currentRecord.maxScale,
                      opacity: currentRecord.opacity,
                      esuSnap: currentRecord.esuSnap,
                      blpuSnap: currentRecord.blpuSnap,
                      extentSnap: currentRecord.extentSnap,
                      createdByUser: currentRecord.createdByUser,
                      globalLayer: currentRecord.globalLayer,
                      serviceProvider: currentRecord.serviceProvider,
                      geometryType: currentRecord.geometryType,
                      layerKey: currentRecord.layerKey,
                      layerUsername: currentRecord.layerUsername,
                      layerPassword: currentRecord.layerPassword,
                      activeLayerId: currentRecord.activeLayerId,
                      serviceMode: currentRecord.serviceMode,
                      propertyName: currentRecord.propertyName,
                      usePaging: currentRecord.usePaging,
                      maxBatchSize: currentRecord.maxBatchSize,
                      thumbnailUrl: currentRecord.thumbnailUrl,
                      mapLayerType: currentRecord.mapLayerType,
                    },
                    {
                      pkId: adjacentRecord.pkId,
                      layerId: adjacentRecord.layerId,
                      layerType: adjacentRecord.layerType,
                      layerPosition: currentLayerPosition,
                      url: adjacentRecord.url,
                      title: adjacentRecord.title,
                      copyright: adjacentRecord.copyright,
                      displayInList: adjacentRecord.displayInList,
                      visible: adjacentRecord.visible,
                      minScale: adjacentRecord.minScale,
                      maxScale: adjacentRecord.maxScale,
                      opacity: adjacentRecord.opacity,
                      esuSnap: adjacentRecord.esuSnap,
                      blpuSnap: adjacentRecord.blpuSnap,
                      extentSnap: adjacentRecord.extentSnap,
                      createdByUser: adjacentRecord.createdByUser,
                      globalLayer: adjacentRecord.globalLayer,
                      serviceProvider: adjacentRecord.serviceProvider,
                      geometryType: adjacentRecord.geometryType,
                      layerKey: adjacentRecord.layerKey,
                      layerUsername: adjacentRecord.layerUsername,
                      layerPassword: adjacentRecord.layerPassword,
                      activeLayerId: adjacentRecord.activeLayerId,
                      serviceMode: adjacentRecord.serviceMode,
                      propertyName: adjacentRecord.propertyName,
                      usePaging: adjacentRecord.usePaging,
                      maxBatchSize: adjacentRecord.maxBatchSize,
                      thumbnailUrl: adjacentRecord.thumbnailUrl,
                      mapLayerType: adjacentRecord.mapLayerType,
                    },
                  ]
                : null;

            if (modifiedRecords) {
              const updatedData = data.map((x) => modifiedRecords.find((rec) => rec.pkId === x.pkId) || x);
              settingsContext.onMapLayersChange(updatedData);
            }
          })
          .catch((res) => {
            switch (res.status) {
              case 400:
                res.json().then((body) => {
                  if (userContext.currentUser.showMessages)
                    console.error(`[400 ERROR] ${isNewLayer ? "Creating" : "Updating"} map layer`, body.errors);
                });
                break;

              case 401:
                useContext.onExpired();
                break;

              default:
                if (userContext.currentUser.showMessages)
                  console.error(
                    `[${res.status} ERROR] HandleDoneEditLayer - ${isNewLayer ? "Creating" : "Updating"} map layer.`,
                    res
                  );

                res.text().then((response) => {
                  if (userContext.currentUser.showMessages)
                    console.error(
                      `[${res.status} ERROR] HandleDoneEditLayer - ${isNewLayer ? "Creating" : "Updating"} map layer.`,
                      response,
                      res
                    );

                  const responseData = response.replace("[{", "").replace("}]", "").split(',"');

                  let errorTitle = "";
                  let errorDescription = "";

                  for (const errorData of responseData) {
                    if (errorData.includes("errorTitle")) errorTitle = errorData.substr(13).replace('"', "");
                    else if (errorData.includes("errorDescription"))
                      errorDescription = errorData.substr(19).replace('"', "");

                    if (errorTitle && errorTitle.length > 0 && errorDescription && errorDescription.length > 0) break;
                  }
                });
                break;
            }
          });
      }
    };

    const onMoveUpClick = (event) => {
      event.stopPropagation();
      moveLayer(params.row.layerPosition, params.row.layerPosition + 1);
    };

    const onMoveDownClick = (event) => {
      event.stopPropagation();
      moveLayer(params.row.layerPosition, params.row.layerPosition - 1);
    };

    const onEditClick = (event) => {
      doEditMapLayer(event, params.row.id);
    };

    const onDeleteClick = (event) => {
      doDeleteMapLayer(event, params.row.id);
    };

    return selectedRow && params.id > 0 && params.id === selectedRow ? (
      <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
        <ADSActionButton
          id={`move-map-layer-up-${params.id}`}
          variant="moveUp"
          disabled={params.row.layerPosition === gridData.length}
          inheritBackground
          tooltipTitle={`Move layer up`}
          tooltipPlacement="bottom"
          onClick={onMoveUpClick}
        />
        <ADSActionButton
          id={`move-map-layer-down-${params.id}`}
          variant="moveDown"
          disabled={params.row.layerPosition === 1}
          inheritBackground
          tooltipTitle={`Move layer down`}
          tooltipPlacement="bottom"
          onClick={onMoveDownClick}
        />
        <ADSActionButton
          id={`edit-map-layer-${params.id}`}
          variant="edit"
          inheritBackground
          tooltipTitle={`Edit layer`}
          tooltipPlacement="bottom"
          onClick={onEditClick}
        />
        {process.env.NODE_ENV === "development" && (
          <ADSActionButton
            id={`delete-map-layer-${params.id}`}
            variant="deleteForever"
            inheritBackground
            tooltipTitle={`Delete layer forever`}
            tooltipPlacement="bottom"
            onClick={onDeleteClick}
          />
        )}
      </Stack>
    ) : null;
  };

  const mapLayerColumns = [
    {
      field: "id",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
    },
    {
      field: "layerType",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
    },
    {
      field: "layerPosition",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
    },
    {
      field: "title",
      headerName: "Layer",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
      display: "flex",
      flex: 70,
      renderCell: GetMapLayer,
    },
    {
      field: "visible",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
    },
    {
      field: "thumbnailUrl",
      headerClassName: "idox-data-grid-header",
      sortable: false,
      filterable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerClassName: "idox-data-grid-header",
      display: "flex",
      sortable: false,
      filterable: false,
      align: "left",
      flex: 15,
      renderCell: displayActionButtons,
    },
  ];

  const sortModel = [{ field: "layerPosition", sort: "desc" }];

  const stackStyle = { ml: theme.spacing(2), mt: theme.spacing(1), width: "55vw" };

  /**
   * Event to handle when the add button is clicked.
   */
  const doAddMapLayer = () => {
    newLayer.current = true;
    if (onAddMap) onAddMap();
  };

  /**
   * Event to handle when a cell is clicked.
   *
   * @param {object} param The parameters passed by the grid.
   * @param {object} event The event object.
   */
  const handleCellClicked = (param, event) => {
    if (param && param.field !== "__check__" && param.field !== "" && param.field !== "actions" && param.id > 0) {
      doEditMapLayer(event, param.row.id);
    }
  };

  /**
   * Event to handle when the edit button is clicked.
   *
   * @param {object} event The event object.
   * @param {number} pkId The id of the map layer.
   */
  const doEditMapLayer = (event, pkId) => {
    event.stopPropagation();
    if (onEditMap) onEditMap(pkId);
  };

  /**
   * Event to handle when the delete button is clicked.
   *
   * @param {object} event The event object.
   * @param {number} pkId The id of the map layer.
   */
  const doDeleteMapLayer = (event, pkId) => {
    event.stopPropagation();
    if (onDeleteMap) onDeleteMap(pkId);
  };

  /**
   * Method to get the map layer display.
   *
   * @param {object} params The parameters supplied by the grid.
   * @returns {JSX.Element} The display of the map layer.
   */
  function GetMapLayer(params) {
    if (params) {
      const mapLayerRecord = data.find((x) => x.pkId === params.id);
      if (mapLayerRecord) {
        if (mapLayerRecord.visible)
          return (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
              <Avatar
                variant={getAvatarVariant(params.row.layerType)}
                sx={getAvatarStyle(params.row.layerType, selectedRow && params.id > 0 && params.id === selectedRow)}
              >
                <LayersIcon />
              </Avatar>
              <Typography variant="body2">{params.value}</Typography>
            </Stack>
          );
        else
          return (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
              <Avatar
                variant={getAvatarVariant(params.row.layerType)}
                sx={getAvatarStyle(params.row.layerType, selectedRow && params.id > 0 && params.id === selectedRow)}
              >
                <LayersIcon />
              </Avatar>
              <Typography variant="body2" sx={{ textDecoration: "line-through" }}>
                {params.value}
              </Typography>
            </Stack>
          );
      }
    }
  }

  /**
   * Event to handle when the mouse enters the row.
   *
   * @param {object} event The event object.
   */
  const handleRowMouseEnter = (event) => {
    event.preventDefault();
    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves the row.
   */
  const handleRowMouseLeave = () => {
    setSelectedRow(null);
  };

  /**
   * Method to determine if the layer is visible or not.
   *
   * @param {number} id The id of the map layer.
   * @returns {boolean} True if the layer is visible; otherwise false.
   */
  const isRowVisible = (id) => {
    if (data && data.length > 0) {
      const visibleRow = data.filter((x) => x.pkId === id && x.visible);
      return visibleRow && visibleRow.length > 0;
    } else return false;
  };

  /**
   * Method to get the avatar variant.
   *
   * @param {number} type The type of map layer.
   * @returns {string} The variant for the avatar.
   */
  const getAvatarVariant = (type) => {
    switch (type) {
      case 1: // WFS
        return "circular";

      case 2: // WMS
        return "square";

      case 3: // WMTS
        return "rounded";

      default: // VTL
        return "circular";
    }
  };

  /**
   * Method to get the styling for the avatar.
   *
   * @param {number} type The type of map layer.
   * @param {boolean} highlighted True if the map layer is highlighted; otherwise false.
   * @returns {object} The styling to use for the avatar.
   */
  const getAvatarStyle = (type, highlighted) => {
    if (highlighted)
      return {
        backgroundColor: adsBlueA,
        width: "24px",
        height: "24px",
        mt: "2px",
      };
    else {
      switch (type) {
        case 1: // WFS
          return {
            backgroundColor: adsLightBlue,
            width: "24px",
            height: "24px",
            mt: "2px",
          };

        case 2: // WMS
          return {
            backgroundColor: adsDarkBlue,
            width: "24px",
            height: "24px",
            mt: "2px",
          };

        case 3: // WMTS
          return {
            backgroundColor: adsMagenta,
            width: "24px",
            height: "24px",
            mt: "2px",
          };

        default: // VTL
          return {
            backgroundColor: adsRed,
            width: "24px",
            height: "24px",
            mt: "2px",
          };
      }
    }
  };

  useEffect(() => {
    newLayer.current = false;
    setSelectedRow(null);

    switch (variant) {
      case "base":
        setTitle("Basemap Gallery");
        setAddTooltip("Add new basemap layer");
        setAddLabel("Basemap");
        break;

      case "reference":
        setTitle("Reference maps");
        setAddTooltip("Add new reference map layer");
        setAddLabel("Reference map");
        break;

      case "custom":
        setTitle("Custom maps");
        setAddTooltip("Add new custom map layer");
        setAddLabel("Custom map");
        break;

      default:
        break;
    }
  }, [variant]);

  useEffect(() => {
    if (data && data.length > 0) {
      setGridData(
        data.map((x) => {
          return {
            id: x.pkId,
            layerType: x.layerType,
            layerPosition: x.layerPosition,
            title: x.title,
            visible: x.visible,
          };
        })
      );
    } else {
      setGridData([
        { id: 0, layerType: "", layerPosition: 0, title: "No map layers have been defined.", visible: false },
      ]);
    }
  }, [data]);

  return (
    <Box sx={{ ml: theme.spacing(1), mr: theme.spacing(4) }}>
      <Stack direction="column" spacing={2} sx={stackStyle}>
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontSize: 24, flexGrow: 1 }}>{title}</Typography>
          {process.env.NODE_ENV === "development" && (
            <Tooltip title={addTooltip} placement="top" sx={tooltipStyle} arrow>
              <Button variant="contained" sx={blueButtonStyle} startIcon={<AddIcon />} onClick={doAddMapLayer}>
                <Typography variant="body2">{addLabel}</Typography>
              </Button>
            </Tooltip>
          )}
        </Stack>
        <Box sx={dataFormStyle("MapLayersTab")}>
          {gridData && gridData.length > 0 && (
            <StyledDataGrid
              rows={gridData}
              columns={mapLayerColumns}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    id: false,
                    layerType: false,
                    layerPosition: false,
                    visible: false,
                    thumbnailUrl: false,
                  },
                },
              }}
              autoPageSize
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              hideFooterSelectedRowCount
              editMode="row"
              pagination
              sortModel={sortModel}
              slotProps={{
                row: {
                  onMouseEnter: handleRowMouseEnter,
                  onMouseLeave: handleRowMouseLeave,
                },
              }}
              getRowClassName={(params) => `${isRowVisible(params.id) ? "visible-row" : "hidden-row"}`}
              onCellClick={handleCellClicked}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default MapLayerGridTab;
